import React, { createContext, useState, useEffect, useRef, useMemo } from "react";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import { GlobalStyles, CssBaseline } from "@mui/material";
import api from "./api";

export const ThemeContext = createContext();

// ── Defaults ──────────────────────────────────────────────────────────────────
const LIGHT_DEFAULTS = {
  sidebarColor: "#2c3e50",
  appBarColor:  "#2f63ad",
  mode:         "light",
};

const DARK_DEFAULTS = {
  sidebarColor: "#0d1b2a",
  appBarColor:  "#1a3a5c",
  mode:         "dark",
};

// ── App font (matches index.css) ──────────────────────────────────────────────
const APP_FONT = "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif";

const getUserId = () => localStorage.getItem("userId") || "guest";
const getThemeKey = () => `customTheme_${getUserId()}`;

const loadStoredTheme = () => {
  try {
    const key = getThemeKey();
    const stored = localStorage.getItem(key);
    if (stored) return { ...LIGHT_DEFAULTS, ...JSON.parse(stored) };

    // Migration: if a legacy "customTheme" key exists, adopt it and remove it
    const legacy = localStorage.getItem("customTheme");
    if (legacy) {
      const parsed = JSON.parse(legacy);
      localStorage.setItem(key, legacy);
      localStorage.removeItem("customTheme");
      return { ...LIGHT_DEFAULTS, ...parsed };
    }
  } catch {}
  return LIGHT_DEFAULTS;
};

// ── MUI theme builder ─────────────────────────────────────────────────────────
const buildMuiTheme = (appTheme) => {
  const dark = appTheme.mode === "dark";
  return createTheme({
    palette: {
      mode: dark ? "dark" : "light",
      primary: { main: appTheme.appBarColor || "#2f63ad" },
      background: dark
        ? { default: "#0f1621", paper: "#16223a" }
        : { default: "#f4f7fb", paper: "#ffffff" },
      text: dark
        ? { primary: "#dde5f0", secondary: "#8a9ab8" }
        : { primary: "#223142", secondary: "#6b778c" },
      divider: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
    },
    shape: { borderRadius: 10 },
    // Restore the app's original font. CssBaseline will use this
    typography: { fontFamily: APP_FONT },
    components: {
      MuiPaper:    { styleOverrides: { root: { backgroundImage: "none" } } },
      MuiCard:     { styleOverrides: { root: { backgroundImage: "none" } } },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: "thin",
            fontFamily: APP_FONT,
          },
        },
      },
      // Ensure outlined buttons get proper theme colours
      MuiButton: {
        styleOverrides: {
          outlined: {
            // Let the theme handle colours; individual dark overrides live in darkStyles
          },
        },
      },
    },
  });
};

// ── Global dark-mode CSS overrides ────────────────────────────────────────────
// These use the [data-theme="dark"] attribute set on <html> to beat
// hardcoded color values in component sx props.
const darkStyles = {
  // ── Body background (overrides linear-gradient in index.css) ──────────────
  // Must use `background` shorthand to cancel the gradient image too
  "body": {
    background:      "#0f1621 !important",
    backgroundColor: "#0f1621 !important",
  },

  // All MUI Paper / Card elements
  '[data-theme="dark"] .MuiPaper-root, [data-theme="dark"] .MuiCard-root': {
    backgroundColor: "#16223a !important",
    background:      "#16223a !important",
    backgroundImage: "none !important",
    borderColor:     "rgba(255,255,255,0.07) !important",
  },

  // Box-based modals (Modal > Box, not Dialog > Paper)
  '[data-theme="dark"] .MuiModal-root > .MuiBox-root': {
    backgroundColor: "#1a2c46 !important",
    background:      "#1a2c46 !important",
  },
  // In case the Box is wrapped in another Box (some modals nest)
  '[data-theme="dark"] .MuiModal-root > * > .MuiBox-root': {
    backgroundColor: "#1a2c46 !important",
    background:      "#1a2c46 !important",
  },

  // ── Typography ─────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiTypography-h1, [data-theme="dark"] .MuiTypography-h2, [data-theme="dark"] .MuiTypography-h3, [data-theme="dark"] .MuiTypography-h4, [data-theme="dark"] .MuiTypography-h5, [data-theme="dark"] .MuiTypography-h6, [data-theme="dark"] .MuiTypography-subtitle1, [data-theme="dark"] .MuiTypography-subtitle2': {
    color: "#dde5f0 !important",
  },
  '[data-theme="dark"] .MuiTypography-body1, [data-theme="dark"] .MuiTypography-body2': {
    color: "#8a9ab8 !important",
  },
  '[data-theme="dark"] .MuiTypography-caption': {
    color: "#6b7c96 !important",
  },

  // ── Inputs ─────────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiOutlinedInput-root': {
    backgroundColor: "rgba(255,255,255,0.05) !important",
    background:      "rgba(255,255,255,0.05) !important",
  },
  '[data-theme="dark"] .MuiOutlinedInput-notchedOutline': {
    borderColor: "rgba(255,255,255,0.14) !important",
  },
  '[data-theme="dark"] .MuiInputBase-input': {
    color: "#dde5f0 !important",
  },
  '[data-theme="dark"] .MuiInputBase-input::placeholder': {
    color: "#8a9ab8 !important",
    opacity: 1,
  },
  '[data-theme="dark"] .MuiInputLabel-root, [data-theme="dark"] .MuiFormLabel-root': {
    color: "#8a9ab8 !important",
  },
  '[data-theme="dark"] .MuiInputLabel-root.Mui-focused, [data-theme="dark"] .MuiFormLabel-root.Mui-focused': {
    color: "#7ab3f0 !important",
  },
  '[data-theme="dark"] .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: "#7ab3f0 !important",
  },
  // Filled textarea background (used in comments / text areas)
  '[data-theme="dark"] .MuiFilledInput-root': {
    backgroundColor: "rgba(255,255,255,0.06) !important",
  },

  // ── Buttons ────────────────────────────────────────────────────────────────
  // Outlined "Cancel" style buttons. fix unreadable dark-on-dark text
  '[data-theme="dark"] .MuiButton-outlined': {
    color:       "#b8c9de !important",
    borderColor: "rgba(255,255,255,0.22) !important",
  },
  '[data-theme="dark"] .MuiButton-outlined:hover': {
    backgroundColor: "rgba(255,255,255,0.06) !important",
    borderColor:     "#7ab3f0 !important",
    color:           "#dde5f0 !important",
  },
  '[data-theme="dark"] .MuiButton-text': {
    color: "#b8c9de !important",
  },
  '[data-theme="dark"] .MuiButton-text:hover': {
    backgroundColor: "rgba(255,255,255,0.06) !important",
  },
  '[data-theme="dark"] .MuiIconButton-root': {
    color: "#8a9ab8 !important",
  },
  '[data-theme="dark"] .MuiIconButton-root:hover': {
    backgroundColor: "rgba(255,255,255,0.07) !important",
  },

  // ── Select / Menu ──────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiSelect-select': { color: "#dde5f0 !important" },
  '[data-theme="dark"] .MuiSelect-icon':   { color: "#8a9ab8 !important" },
  '[data-theme="dark"] .MuiPopover-paper, [data-theme="dark"] .MuiMenu-paper': {
    backgroundColor: "#1e2f48 !important",
    background:      "#1e2f48 !important",
  },
  '[data-theme="dark"] .MuiMenuItem-root': { color: "#dde5f0 !important" },
  '[data-theme="dark"] .MuiMenuItem-root:hover': {
    backgroundColor: "rgba(255,255,255,0.06) !important",
  },
  '[data-theme="dark"] .MuiMenuItem-root.Mui-selected': {
    backgroundColor: "rgba(122,179,240,0.15) !important",
  },

  // ── Tables ─────────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiTableCell-root': {
    color:           "#b8c9de !important",
    borderColor:     "rgba(255,255,255,0.06) !important",
    backgroundColor: "transparent !important",
  },
  '[data-theme="dark"] .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: "rgba(255,255,255,0.04) !important",
    color:           "#dde5f0 !important",
  },
  '[data-theme="dark"] .MuiTableBody-root .MuiTableRow-root:hover': {
    backgroundColor: "rgba(255,255,255,0.03) !important",
  },
  '[data-theme="dark"] .MuiTablePagination-root, [data-theme="dark"] .MuiTablePagination-select, [data-theme="dark"] .MuiTablePagination-displayedRows, [data-theme="dark"] .MuiTablePagination-selectLabel': {
    color: "#8a9ab8 !important",
  },

  // ── DataGrid ───────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiDataGrid-root': {
    borderColor:     "rgba(255,255,255,0.08) !important",
    backgroundColor: "#16223a !important",
  },
  '[data-theme="dark"] .MuiDataGrid-cell': {
    color:       "#b8c9de !important",
    borderColor: "rgba(255,255,255,0.06) !important",
  },
  '[data-theme="dark"] .MuiDataGrid-columnHeaders': {
    backgroundColor: "rgba(255,255,255,0.04) !important",
    borderColor:     "rgba(255,255,255,0.08) !important",
  },
  '[data-theme="dark"] .MuiDataGrid-columnHeaderTitle': {
    color: "#dde5f0 !important",
  },
  '[data-theme="dark"] .MuiDataGrid-row:hover': {
    backgroundColor: "rgba(255,255,255,0.03) !important",
  },
  '[data-theme="dark"] .MuiDataGrid-footerContainer, [data-theme="dark"] .MuiDataGrid-toolbarContainer': {
    backgroundColor: "#16223a !important",
    borderColor:     "rgba(255,255,255,0.08) !important",
    color:           "#dde5f0 !important",
  },
  '[data-theme="dark"] .MuiDataGrid-sortIcon, [data-theme="dark"] .MuiDataGrid-filterIcon': {
    color: "#8a9ab8 !important",
  },
  '[data-theme="dark"] .MuiDataGrid-iconSeparator': {
    color: "rgba(255,255,255,0.1) !important",
  },

  // ── List / Sidebar ─────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiListItemButton-root:hover': {
    backgroundColor: "rgba(255,255,255,0.05) !important",
  },
  '[data-theme="dark"] .MuiListItemText-primary':   { color: "#dde5f0 !important" },
  '[data-theme="dark"] .MuiListItemText-secondary': { color: "#8a9ab8 !important" },

  // ── Tabs ───────────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiTab-root': { color: "#8a9ab8 !important" },
  '[data-theme="dark"] .MuiTab-root.Mui-selected': {
    color:           "#7ab3f0 !important",
    backgroundColor: "rgba(122,179,240,0.1) !important",
  },
  '[data-theme="dark"] .MuiTabs-indicator': {
    backgroundColor: "#7ab3f0 !important",
  },

  // ── Toggle buttons ─────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiToggleButton-root': {
    color:       "#8a9ab8 !important",
    borderColor: "rgba(255,255,255,0.12) !important",
  },
  '[data-theme="dark"] .MuiToggleButton-root.Mui-selected': {
    backgroundColor: "rgba(122,179,240,0.15) !important",
    color:           "#7ab3f0 !important",
  },

  // ── Chips ──────────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiChip-colorDefault': {
    backgroundColor: "rgba(255,255,255,0.12) !important",
    color:           "#b8c9de !important",
  },
  '[data-theme="dark"] .MuiChip-label': {
    color: "inherit !important",
  },

  // ── Divider ────────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiDivider-root': {
    borderColor: "rgba(255,255,255,0.08) !important",
  },

  // ── Dialog ─────────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiDialog-paper': {
    backgroundColor: "#1a2c46 !important",
    background:      "#1a2c46 !important",
    borderColor:     "rgba(255,255,255,0.08) !important",
  },
  '[data-theme="dark"] .MuiDialogTitle-root':       { color: "#dde5f0 !important" },
  '[data-theme="dark"] .MuiDialogContent-root':     { color: "#8a9ab8 !important" },
  '[data-theme="dark"] .MuiDialogContentText-root': { color: "#8a9ab8 !important" },
  '[data-theme="dark"] .MuiDialogActions-root': {
    borderTop: "1px solid rgba(255,255,255,0.07) !important",
  },

  // ── Dropzone / upload area ─────────────────────────────────────────────────
  '[data-theme="dark"] .MuiPaper-outlined': {
    borderColor: "rgba(255,255,255,0.14) !important",
  },

  // ── Skeleton ───────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiSkeleton-root': {
    backgroundColor: "rgba(255,255,255,0.08) !important",
  },

  // ── Alerts ─────────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiAlert-standardInfo':    { backgroundColor: "rgba(41,128,185,0.18) !important" },
  '[data-theme="dark"] .MuiAlert-standardWarning': { backgroundColor: "rgba(243,156,18,0.18) !important" },
  '[data-theme="dark"] .MuiAlert-standardError':   { backgroundColor: "rgba(231,76,60,0.18) !important" },
  '[data-theme="dark"] .MuiAlert-standardSuccess': { backgroundColor: "rgba(46,204,113,0.18) !important" },

  // ── Accordion ─────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiAccordion-root': {
    backgroundColor: "#16223a !important",
    background:      "#16223a !important",
    borderColor:     "rgba(255,255,255,0.08) !important",
  },
  '[data-theme="dark"] .MuiAccordionSummary-root': {
    color: "#dde5f0 !important",
  },

  // ── Collapse / expansion containers ───────────────────────────────────────
  '[data-theme="dark"] .MuiCollapse-root .MuiPaper-root': {
    backgroundColor: "#1a2c46 !important",
    background:      "#1a2c46 !important",
  },

  // ── Breadcrumbs ────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiBreadcrumbs-root, [data-theme="dark"] .MuiBreadcrumbs-li': {
    color: "#8a9ab8 !important",
  },

  // ── Tooltip ────────────────────────────────────────────────────────────────
  '[data-theme="dark"] .MuiTooltip-tooltip': {
    backgroundColor: "#1e2f48 !important",
    color:           "#dde5f0 !important",
    border:          "1px solid rgba(255,255,255,0.1) !important",
  },

  // ── react-big-calendar ─────────────────────────────────────────────────────
  // The imported rbc CSS file sets background/border via plain .rbc-* selectors.
  // We need !important here to beat that global stylesheet.
  '[data-theme="dark"] .rbc-calendar': {
    backgroundColor: "#16223a !important",
    color:           "#dde5f0 !important",
  },
  '[data-theme="dark"] .rbc-toolbar button': {
    background:   "#1a2c46 !important",
    color:        "#7ab3f0 !important",
    borderColor:  "rgba(255,255,255,0.12) !important",
    borderRadius: "10px !important",
  },
  '[data-theme="dark"] .rbc-toolbar button:hover': {
    background:  "rgba(122,179,240,0.1) !important",
    borderColor: "rgba(122,179,240,0.3) !important",
  },
  '[data-theme="dark"] .rbc-toolbar button.rbc-active, [data-theme="dark"] .rbc-toolbar button.rbc-active:hover': {
    background:  "#2f63ad !important",
    color:       "#fff !important",
    borderColor: "#2f63ad !important",
  },
  '[data-theme="dark"] .rbc-toolbar-label': {
    color: "#dde5f0 !important",
  },
  '[data-theme="dark"] .rbc-month-view, [data-theme="dark"] .rbc-time-view, [data-theme="dark"] .rbc-agenda-view': {
    backgroundColor: "#16223a !important",
    background:      "#16223a !important",
    borderColor:     "rgba(255,255,255,0.08) !important",
  },
  '[data-theme="dark"] .rbc-header': {
    backgroundColor: "rgba(255,255,255,0.04) !important",
    background:      "rgba(255,255,255,0.04) !important",
    color:           "#8a9ab8 !important",
    borderColor:     "rgba(255,255,255,0.08) !important",
  },
  // Individual day cells
  '[data-theme="dark"] .rbc-day-bg': {
    backgroundColor: "#16223a !important",
    background:      "#16223a !important",
  },
  '[data-theme="dark"] .rbc-off-range-bg': {
    backgroundColor: "rgba(0,0,0,0.18) !important",
    background:      "rgba(0,0,0,0.18) !important",
  },
  '[data-theme="dark"] .rbc-today': {
    backgroundColor: "rgba(122,179,240,0.08) !important",
    background:      "rgba(122,179,240,0.08) !important",
  },
  '[data-theme="dark"] .rbc-date-cell': { color: "#8a9ab8 !important" },
  '[data-theme="dark"] .rbc-date-cell a': { color: "#8a9ab8 !important" },
  // Row / slot borders
  '[data-theme="dark"] .rbc-month-row, [data-theme="dark"] .rbc-row-bg': {
    borderColor: "rgba(255,255,255,0.06) !important",
  },
  '[data-theme="dark"] .rbc-time-content, [data-theme="dark"] .rbc-timeslot-group, [data-theme="dark"] .rbc-time-header, [data-theme="dark"] .rbc-time-header-content': {
    borderColor:     "rgba(255,255,255,0.06) !important",
    backgroundColor: "#16223a !important",
    background:      "#16223a !important",
  },
  '[data-theme="dark"] .rbc-time-header-gutter, [data-theme="dark"] .rbc-time-gutter': {
    backgroundColor: "#16223a !important",
    background:      "#16223a !important",
    borderColor:     "rgba(255,255,255,0.06) !important",
  },
  '[data-theme="dark"] .rbc-label, [data-theme="dark"] .rbc-time-slot': {
    color:       "#8a9ab8 !important",
    borderColor: "rgba(255,255,255,0.04) !important",
  },
  '[data-theme="dark"] .rbc-current-time-indicator': {
    backgroundColor: "#7ab3f0 !important",
  },
  // Agenda view
  '[data-theme="dark"] .rbc-agenda-view table': {
    borderColor: "rgba(255,255,255,0.08) !important",
  },
  '[data-theme="dark"] .rbc-agenda-table': {
    borderColor: "rgba(255,255,255,0.08) !important",
  },
  '[data-theme="dark"] .rbc-agenda-table thead th': {
    backgroundColor: "rgba(255,255,255,0.04) !important",
    background:      "rgba(255,255,255,0.04) !important",
    color:           "#8a9ab8 !important",
    borderColor:     "rgba(255,255,255,0.08) !important",
  },
  '[data-theme="dark"] .rbc-agenda-date-cell, [data-theme="dark"] .rbc-agenda-time-cell': {
    color:           "#8a9ab8 !important",
    borderColor:     "rgba(255,255,255,0.06) !important",
    backgroundColor: "#16223a !important",
  },
  '[data-theme="dark"] .rbc-agenda-event-cell': {
    borderColor: "rgba(255,255,255,0.06) !important",
    color:       "#b8c9de !important",
  },
  '[data-theme="dark"] .rbc-show-more': {
    color:           "#7ab3f0 !important",
    backgroundColor: "transparent !important",
  },
  '[data-theme="dark"] .rbc-overlay': {
    backgroundColor: "#1a2c46 !important",
    background:      "#1a2c46 !important",
    borderColor:     "rgba(255,255,255,0.1) !important",
  },
  '[data-theme="dark"] .rbc-overlay-header': {
    color: "#dde5f0 !important",
    borderColor: "rgba(255,255,255,0.08) !important",
  },

  // ── Scrollbars ─────────────────────────────────────────────────────────────
  '[data-theme="dark"] *::-webkit-scrollbar-track': {
    backgroundColor: "#0f1621",
  },
  '[data-theme="dark"] *::-webkit-scrollbar-thumb': {
    backgroundColor: "rgba(255,255,255,0.15)",
    borderRadius:    "4px",
  },
  '[data-theme="dark"] *::-webkit-scrollbar-thumb:hover': {
    backgroundColor: "rgba(255,255,255,0.25)",
  },
};

// ── Provider ──────────────────────────────────────────────────────────────────
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(loadStoredTheme);

  // Holds the full preferences object so saving never clobbers other fields
  const fullPreferencesRef = useRef(null);
  // Prevents saving back to API the value we just loaded from it
  const loadedFromApiRef = useRef(false);

  // ── Fetch user preferences from API on mount ────────────────────────────────
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!userId || userId === "guest") return;

    api
      .get(`/user-preferences/${userId}`)
      .then(({ data }) => {
        const prefs = data?.preferences || {};
        fullPreferencesRef.current = prefs;

        const apiMode = prefs.theme === "dark" ? "dark" : prefs.theme === "light" ? "light" : null;
        if (!apiMode) return; // no theme preference stored yet

        setTheme((prev) => {
          if (prev.mode === apiMode) return prev; // already correct, skip re-render
          loadedFromApiRef.current = true;
          const base = apiMode === "dark" ? DARK_DEFAULTS : LIGHT_DEFAULTS;
          return { ...base, ...prev, mode: apiMode };
        });
      })
      .catch(() => {
        // Not logged in yet or endpoint unavailable. keep localStorage value
      });
  }, []); // runs once on mount; see reinitializeForUser for post-login refresh

  // ── Keep localStorage in sync ───────────────────────────────────────────────
  useEffect(() => {
    localStorage.setItem(getThemeKey(), JSON.stringify(theme));
  }, [theme]);

  // ── Apply data-theme attribute to <html> so CSS selectors work ──────────────
  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      theme.mode === "dark" ? "dark" : "light"
    );
  }, [theme.mode]);

  // ── Save mode change back to API ────────────────────────────────────────────
  useEffect(() => {
    // Skip the first write that came FROM the API (don't echo it back)
    if (loadedFromApiRef.current) {
      loadedFromApiRef.current = false;
      return;
    }

    const userId = localStorage.getItem("userId");
    if (!userId || userId === "guest") return;

    // Merge updated theme into the full preferences object (flat body. no wrapper)
    const updatedPrefs = {
      ...(fullPreferencesRef.current || {}),
      theme: theme.mode,
    };
    fullPreferencesRef.current = updatedPrefs;

    api
      .put(`/user-preferences/${userId}`, updatedPrefs)
      .catch((err) => console.warn("Failed to save theme preference:", err));
  }, [theme.mode]);

  // ── Public API ───────────────────────────────────────────────────────────────
  const updateTheme = (newValues) =>
    setTheme((prev) => ({ ...prev, ...newValues }));

  const reinitializeForUser = () => {
    // Called after login. reload from localStorage then re-fetch from API
    setTheme(loadStoredTheme());

    const userId = localStorage.getItem("userId");
    if (!userId || userId === "guest") return;

    api
      .get(`/user-preferences/${userId}`)
      .then(({ data }) => {
        // GET returns { preferences: { theme, ... }, isDefault }
        const prefs = data?.preferences || {};
        fullPreferencesRef.current = prefs;

        const apiMode = prefs.theme === "dark" ? "dark" : prefs.theme === "light" ? "light" : null;
        if (!apiMode) return;

        setTheme((prev) => {
          if (prev.mode === apiMode) return prev;
          loadedFromApiRef.current = true;
          const base = apiMode === "dark" ? DARK_DEFAULTS : LIGHT_DEFAULTS;
          return { ...base, ...prev, mode: apiMode };
        });
      })
      .catch(() => {});
  };

  const muiTheme = useMemo(() => buildMuiTheme(theme), [theme.mode, theme.appBarColor]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      {theme.mode === "dark" && <GlobalStyles styles={darkStyles} />}
      <ThemeContext.Provider value={{ theme, updateTheme, isDark: theme.mode === "dark", reinitializeForUser }}>
        {children}
      </ThemeContext.Provider>
    </MuiThemeProvider>
  );
};
